import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { FormattedMessage, intlShape } from 'react-intl'

import AirDnaAdditionalInfo from './AdditionalInfo'
import AddressForm from './AddressForm'
import ChartControls from './ChartControls'
import FactTable from './FactTable'
import ChartTable from './ChartTable'
import { Button, Panel, Format, If, Text } from 'src/Components'
import { AIR_DNA } from 'src/constants'
import { AIRDNA_QUALITY } from 'src/constants/messages'
import { IconCheckCircle } from 'src/Components/shared/Icon'
import BotHeadColored from 'src/Components/shared/Avatar/BotHeadColored'
import { RENTAL_POTENTIAL } from 'src/constants/document_ids'

import styles from './AirDna.module.scss'

const propTypes = {
  intl: intlShape,
  showExtraPayments: PropTypes.bool.isRequired,
  updateInfo: PropTypes.func,
  paymentMax: PropTypes.number,
  pricePerNight: PropTypes.number,
  onSubmitExample: PropTypes.func,
  extraPaymentSavings: PropTypes.number,
  extraPaymentYears: PropTypes.number,
  params: PropTypes.PropTypes.shape({
    address: PropTypes.string,
    zipcode: PropTypes.string
  }),
  corporateProfile: PropTypes.shape({
    data: PropTypes.shape({
      propertyStats: PropTypes.shape({
        adr: PropTypes.shape({
          ltm: PropTypes.number
        }),
        occupancy: PropTypes.shape({
          ltm: PropTypes.number
        }),
        revenue: PropTypes.shape({
          ltm: PropTypes.number
        })
      })
    })
  }),
  hideEdit: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string,
    name: PropTypes.string,
    stack: PropTypes.string
  })
}

const defaultProps = {
  updateInfo: noop
}

const BARS = [
  // [height, hideMobile]
  ['25%', true],
  ['65%'],
  ['78%', true],
  ['62%'],
  ['72%'],
  ['66%', true],
  ['55%'],
  ['45%', true],
  ['85%'],
  ['40%', true],
  ['63%'],
  ['70%', true],
  ['35%']
]

const defaultAddress = `${AIR_DNA.DEFAULT_ADDRESS.streetAddress}, ${
  AIR_DNA.DEFAULT_ADDRESS.city
} ${AIR_DNA.DEFAULT_ADDRESS.state} ${AIR_DNA.DEFAULT_ADDRESS.zipcode}`

class AirDna extends Component {
  static propTypes = propTypes
  static defaultProps = defaultProps

  getRentalQualityMessage () {
    const { paymentMax, pricePerNight } = this.props
    const nightlyRate = pricePerNight
      ? parseFloat(parseInt(pricePerNight, 10) / paymentMax)
      : 1

    if (nightlyRate >= 0.75) {
      return AIRDNA_QUALITY.fancy
    }

    if (nightlyRate < 0.4) {
      return AIRDNA_QUALITY.basic
    }

    return AIRDNA_QUALITY.nice
  }

  renderBars () {
    return (
      <div className={styles.loadingContainer}>
        <ul className={styles.loadingChart}>
          {BARS.map(([height, hideMobile]) => (
            <li key={height} className={hideMobile ? styles.hidemobile : null}>
              <span style={{ height }} title='' />
            </li>
          ))}
        </ul>
      </div>
    )
  }

  renderEmpty () {
    const { onSubmitExample } = this.props

    return (
      <>
        <h3 className={styles.exampleAddress}>
          <FormattedMessage
            id='AirDNA.trySampleAddress'
            defaultMessage='Try {defaultAddress}'
            values={{
              defaultAddress: (
                <Button minimal onClick={onSubmitExample}>
                  {defaultAddress}
                </Button>
              )
            }}
          />
        </h3>
        {this.renderBars()}
      </>
    )
  }

  renderExtraPayments () {
    const { extraPaymentSavings, extraPaymentYears } = this.props

    const savingsText = (
      <Text color='green' element='span'>
        <Format.Currency value={Math.round(extraPaymentSavings)} />
      </Text>
    )

    const yearsHighlightText = (
      <Text color='green' element='span'>
        <FormattedMessage
          id='AirDNA.extraPayments.yearsRemoved.highlight'
          defaultMessage='{yearsSavedFormatted} {yearsSaved, plural, one {year} other {years}} off'
          values={{
            yearsSavedFormatted: <Format.Number value={extraPaymentYears} />,
            yearsSaved: extraPaymentYears
          }}
        />
      </Text>
    )

    return (
      <div className={`callout ${styles.extraPaymentsContainer}`}>
        <div className='callout__icon'>
          <BotHeadColored size='xs' className={styles.bot} />
        </div>
        <Text className={`callout__explanation ${styles.extraPaymentsText}`}>
          <FormattedMessage
            id='AirDNA.extraPayments.towardPrincipal'
            defaultMessage="If you paid that money towards your principal, you'd save:"
          />
        </Text>
        <ul className='callout__list list-icons'>
          <li className={styles.extraPaymentItem}>
            <IconCheckCircle className='icon-li text-positive' />
            <Text color='quiet'>
              <FormattedMessage
                id='AirDNA.extraPayments.interestSavings'
                defaultMessage='{savingsText} in interest'
                values={{ savingsText }}
              />
            </Text>
          </li>
          <li className={styles.extraPaymentItem}>
            <IconCheckCircle className='icon-li text-positive' />
            <Text color='quiet'>
              <FormattedMessage
                id='AirDNA.extraPayments.yearsRemoved'
                defaultMessage='{yearsHighlightText} your loan'
                values={{ yearsHighlightText }}
              />
            </Text>
          </li>
        </ul>
      </div>
    )
  }

  renderBody () {
    const { showExtraPayments, intl, params } = this.props
    const qualityMessage = this.getRentalQualityMessage()
    const address = params
      ? `${params.address}, ${params.zipcode}`
      : `${AIR_DNA.DEFAULT_ADDRESS.streetAddress}, ${
          AIR_DNA.DEFAULT_ADDRESS.zipcode
        }`

    return (
      <>
        <h3>
          <FormattedMessage
            id='AirDna.rentalQuality.address'
            defaultMessage='Showing results for {address}'
            values={{ address }}
          />
        </h3>

        <ChartTable />

        <h3>
          <FormattedMessage
            id='AirDna.rentalQuality.headline'
            defaultMessage='Estimated based on similar rentals nearby, and if you made it a {rentalQuality} rental.'
            values={{
              rentalQuality: (
                <strong>{intl.formatMessage(qualityMessage)}</strong>
              )
            }}
          />
        </h3>

        <ChartControls />
        <FactTable />
        <If condition={showExtraPayments}>{this.renderExtraPayments()}</If>
        <If condition={this.props.corporateProfile}>
          <AirDnaAdditionalInfo />
        </If>
      </>
    )
  }

  renderError () {
    return (
      <Panel data-qa='AirDnaCard'>
        <Panel.Heading className={styles.heading} id={RENTAL_POTENTIAL}>
          <FormattedMessage
            id='AirDna.totally.hosed'
            defaultMessage="Sorry, we don't have short term rental information on this property right now. Please check back soon."
          />
        </Panel.Heading>
        {this.renderBars()}
      </Panel>
    )
  }

  render () {
    const { hideEdit, params, error } = this.props

    if (error) {
      return this.renderError()
    }

    return (
      <Panel data-qa='AirDnaCard'>
        <If condition={hideEdit}>
          <Panel.Heading className={styles.heading} id={RENTAL_POTENTIAL}>
            <FormattedMessage
              id='AirDna.withoutAddress.headline'
              defaultMessage='If you rented your home on Airbnb, how much could you potentially earn?'
            />
          </Panel.Heading>
        </If>
        <If condition={!hideEdit}>
          <AddressForm />
        </If>
        {params ? this.renderBody() : this.renderEmpty()}
      </Panel>
    )
  }
}

export default AirDna
